import { useState, useEffect } from 'react';
import api from '../services/api';
import { REGION_META } from '../data/team';

// Monthly QC report — pick a month, get one PDF per region to send to that
// region's manager. Months are bucketed server-side in the Salesforce org's
// timezone, so what you see here matches the QC page's own counts.
//
// jsPDF is pulled in on first download rather than imported up front — it's ~700KB
// and only this modal ever needs it (same treatment @arcgis/core gets in MapView).
const pdfLib = () => import('../utils/qcReportPdf');

const regionHex = (r) => (REGION_META[r] && REGION_META[r].hex) || '#8A949A';
const titleCase = (s) => String(s || '').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const thisMonth = () => new Date().toISOString().slice(0, 7);

const monthName = (month, opts) => {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', opts);
};
const prevMonthName = (month) => {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 2, 1).toLocaleDateString('en-US', { month: 'long' });
};

// "+3" red / "-2" green / "even" — more flags is worse.
function Delta({ now, then }) {
  const d = now - then;
  if (d === 0) return <span className="text-gray-500">even</span>;
  return (
    <span style={{ color: d > 0 ? '#f87171' : '#4ade80' }}>
      {d > 0 ? `+${d}` : d}
    </span>
  );
}

export default function QCReportModal({ onClose }) {
  const [month, setMonth] = useState(thisMonth());
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [zipping, setZipping] = useState(false);
  const [busyRegion, setBusyRegion] = useState(null);

  useEffect(() => {
    let stale = false;
    setReport(null);
    setError('');
    api.get(`/qc/report?month=${month}`)
      .then((res) => { if (!stale) setReport(res.data); })
      .catch(() => { if (!stale) setError("Couldn't load the report. Try again."); });
    return () => { stale = true; };
  }, [month]);

  async function downloadOne(region) {
    setBusyRegion(region.region);
    try {
      const { downloadRegionPdf } = await pdfLib();
      downloadRegionPdf({ region, month, generated_at: report.generated_at, company: report.company });
    } catch {
      setError(`Couldn't build the ${titleCase(region.region)} PDF. Try again.`);
    }
    setBusyRegion(null);
  }

  async function downloadAll() {
    if (!report?.regions?.length) return;
    setZipping(true);
    try {
      const { downloadAllRegionsZip } = await pdfLib();
      await downloadAllRegionsZip(report);
    } catch {
      setError('Building the zip failed. Try downloading regions individually.');
    }
    setZipping(false);
  }

  const regions = report?.regions || [];
  // "Unassigned" is almost always a name mismatch rather than a real region, so
  // name the people involved instead of leaving a mystery bucket in the list.
  const unassigned = regions.find((r) => r.region === 'UNASSIGNED' && r.flags > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={onClose}>
      <div
        className="border rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col"
        style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 pb-0">
          <div>
            <h2 className="text-lg font-black text-white tracking-widest uppercase">Monthly QC Report</h2>
            <p className="text-xs text-gray-500 mt-1">One PDF per region, ready to send to the regional managers</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xl leading-none px-2" title="Close">✕</button>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 p-5 flex-wrap">
          <label className="text-xs text-gray-400 uppercase tracking-wide">Month</label>
          <input
            type="month"
            value={month}
            max={thisMonth()}
            onChange={(e) => e.target.value && setMonth(e.target.value)}
            className="rounded px-3 py-1.5 text-sm text-white border focus:outline-none"
            style={{ backgroundColor: '#142236', borderColor: '#1e3a5f', colorScheme: 'dark' }}
          />
          <button
            onClick={downloadAll}
            disabled={!regions.length || zipping}
            className="ml-auto text-xs px-3 py-2 rounded font-bold uppercase tracking-wide transition disabled:opacity-50"
            style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
          >
            {zipping ? 'Building…' : `Download All (${regions.length}) as ZIP`}
          </button>
        </div>

        {/* Body */}
        <div className="px-5 pb-5 overflow-y-auto">
          {error && <p className="text-red-400 text-sm">{error}</p>}
          {!error && !report && <p className="text-gray-500 text-sm">Loading…</p>}

          {report && regions.length === 0 && (
            <p className="text-gray-500 text-sm italic">
              No QC tickets in {monthName(month, { month: 'long', year: 'numeric' })}.
            </p>
          )}

          {report && regions.length > 0 && (
            <>
              <p className="text-xs text-gray-500 mb-3">
                {report.company.flags} flag{report.company.flags === 1 ? '' : 's'} company-wide in{' '}
                {monthName(month, { month: 'long', year: 'numeric' })} · {report.company.open} still open ·{' '}
                <Delta now={report.company.flags} then={report.company.prevFlags} /> vs {prevMonthName(month)}
              </p>

              <div className="space-y-2">
                {regions.map((r) => {
                  const hex = regionHex(r.region);
                  return (
                    <div
                      key={r.region}
                      className="flex items-center gap-3 border rounded-lg px-4 py-3"
                      style={{ backgroundColor: '#142236', borderColor: '#1e3a5f', borderLeftColor: hex, borderLeftWidth: 3 }}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: hex }} />
                          <span className="text-sm font-bold text-white truncate">{titleCase(r.region)}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {r.flags} flag{r.flags === 1 ? '' : 's'}
                          {r.open > 0 && <span className="text-yellow-500"> · {r.open} open</span>}
                          {' · '}
                          <Delta now={r.flags} then={r.prevFlags} /> vs {prevMonthName(month)}
                          {r.offenders.length > 0 && ` · ${r.offenders.length} PM${r.offenders.length === 1 ? '' : 's'}`}
                        </p>
                      </div>
                      <button
                        onClick={() => downloadOne(r)}
                        disabled={busyRegion === r.region}
                        className="shrink-0 text-xs px-3 py-2 rounded font-bold uppercase tracking-wide border transition hover:bg-white/5 disabled:opacity-50"
                        style={{ color: hex, borderColor: hex }}
                      >
                        {busyRegion === r.region ? '…' : 'PDF'}
                      </button>
                    </div>
                  );
                })}
              </div>

              {unassigned && (
                <p className="text-xs mt-3 px-3 py-2 rounded" style={{ backgroundColor: '#1c2a1a', color: '#a3b18a' }}>
                  <span className="font-bold">Unassigned:</span>{' '}
                  {unassigned.offenders.map((o) => o.tech).join(', ')} — these names don't match anyone in the HR
                  directory, so their flags aren't counted in any region's report. Fix the PM name on the QC page,
                  or add them in HR.
                </p>
              )}

              <p className="text-xs text-gray-600 mt-4">
                Each PDF covers one region: summary, category breakdown vs {prevMonthName(month)}, per-PM totals,
                and every ticket with its note. Regions with no flags this month are only listed if they had some last month.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
